import { Box } from '@mui/material';
import { useIntl } from 'react-intl';

import {
  ApiWorkflowDialog,
  type ApiWorkflowDialogMessages,
  TextField,
} from '@verantos-dev/shared-components';

import { useCreateAnalyticalDatasetDialog } from '../hooks/useCreateAnalyticalDatasetDialog';
import type { CreatedAnalyticalDataset } from '../lib/api/useCreateAnalyticalDatasetMutation';

const NAME_MAX_CHARACTERS = 60;

interface CreateAnalyticalDatasetDialogProps {
  projectId: string;
  onClose: () => void;
  /** Fired with the created profile before close so the caller can route
   *  into the new dataset's detail page. */
  onCreated: (profile: CreatedAnalyticalDataset) => void;
}

/**
 * Inline create dialog opened from the artifacts page Create menu. Lives here
 * (not reused from feature-analytical-datasets) to avoid a dependency cycle.
 * Reuses the `analyticalDatasetsList.createModal.*` message ids — registered
 * app-wide via `analyticalDatasetsMessageKeysEn` — so copy stays in sync with
 * the standalone modal without importing the feature lib.
 */
export const CreateAnalyticalDatasetDialog = ({
  projectId,
  onClose,
  onCreated,
}: CreateAnalyticalDatasetDialogProps) => {
  const { formatMessage } = useIntl();
  const { name, onChangeName, onSubmit, closeDialog, isPending, submittingError, isValidToSubmit } =
    useCreateAnalyticalDatasetDialog({ projectId, onClose, onCreated });

  const dialogMessages: ApiWorkflowDialogMessages = {
    entryCancelButtonText: formatMessage({ id: 'analyticalDatasetsList.createModal.cancelButton' }),
    entrySaveButtonText: formatMessage({ id: 'analyticalDatasetsList.createModal.saveButton' }),
    submittingCancelButtonText: formatMessage({
      id: 'analyticalDatasetsList.createModal.cancelButton',
    }),
    submittingSaveButtonText: formatMessage({
      id: 'analyticalDatasetsList.createModal.submitting',
    }),
    successCloseButtonText: formatMessage({ id: 'analyticalDatasetsList.createModal.closeButton' }),
    submittingErrorTitle: formatMessage({
      id: 'analyticalDatasetsList.createModal.submittingError',
    }),
    successTitle: formatMessage({ id: 'analyticalDatasetsList.createModal.success.title' }),
    successText: formatMessage({ id: 'analyticalDatasetsList.createModal.success.text' }, { name }),
  };

  return (
    <ApiWorkflowDialog
      id="createAnalyticalDatasetDialog"
      title={formatMessage({ id: 'analyticalDatasetsList.createModal.title' })}
      dialogMessages={dialogMessages}
      onClose={closeDialog}
      onSubmit={onSubmit}
      onRetrySubmit={onSubmit}
      isValidForSubmit={isValidToSubmit}
      isSubmitting={isPending}
      submittingError={submittingError}
      hideDisplayButtonsOnSuccess
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
        <TextField
          id="analytical-dataset-name-input"
          displayLabel={formatMessage({ id: 'analyticalDatasetsList.createModal.name.label' })}
          value={name}
          placeholder={formatMessage({
            id: 'analyticalDatasetsList.createModal.name.placeholder',
          })}
          onChange={onChangeName}
          maxCharacters={NAME_MAX_CHARACTERS}
          maxCharacterErrorMsg={formatMessage({
            id: 'analyticalDatasetsList.createModal.maxCharacterErrorMsg',
          })}
          displayNumCharacter
          width="auto"
          beepMaxCharacters
        />
      </Box>
    </ApiWorkflowDialog>
  );
};

export default CreateAnalyticalDatasetDialog;
